/**
 * @file price-update-request-detail.tsx
 * @module app/modules/price-update-request/price-update-request-detail
 * @description Implements the price update request detail workflow for price-update requests.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../Global/loader";
import {
  All_Products,
  View,
  Edit,
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  Piece,
  PriceRequestAccepted,
  PriceRequestRejected,
  PriceRequestSubmitted,
} from "../../../utils/constants";
import Method from "../../../utils/methods";
import { useAuth } from "../auth";
import APICallService from "../../../api/apiCallService";
import { priceUpdateRequest } from "../../../api/apiEndPoints";
import { useWarehouse } from "../../context/WarehouseContext";
import { success, error } from "../../../Global/toast";
import carton from "../../../_admin/assets/media/product/cartoon.png";

const PriceUpdateRequestDetail = () => {
  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { state }: any = useLocation();
  const [fetchLoader, setFetchLoader] = useState(true);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<any>(null);

  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      await fetchDetails();
      setFetchLoader(false);
    })();
  }, []);

  const fetchDetails = async () => {
    if (!state?.id) {
      error("Invalid request ID");
      navigate("/price-update-request");
      return;
    }
    let apiService = new APICallService(
      priceUpdateRequest.info,
      state.id,
      "",
      "",
      "",
      "",
      All_Products,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    if (response) {
      setDetails(response.record || response);
    } else {
      error("Failed to fetch price update request details");
      navigate("/price-update-request");
    }
  };

  const handleApprove = async () => {
    if (
      !window.confirm(
        "Are you sure you want to approve this price update request?"
      )
    ) {
      return;
    }
    setLoading(true);
    const apiService = new APICallService(
      priceUpdateRequest.approve,
      {},
      { id: state.id },
      "",
      "",
      "",
      All_Products,
      selectedWarehouse?.value || null
    );
    const response = await apiService.callAPI();
    if (response) {
      success("Price update request approved successfully");
      await fetchDetails();
    } else {
      error("Failed to approve price update request");
    }
    setLoading(false);
  };

  const handleReject = async () => {
    if (
      !window.confirm(
        "Are you sure you want to reject this price update request?"
      )
    ) {
      return;
    }
    setLoading(true);
    const apiService = new APICallService(
      priceUpdateRequest.reject,
      {},
      { id: state.id },
      "",
      "",
      "",
      All_Products,
      selectedWarehouse?.value || null
    );
    const response = await apiService.callAPI();
    if (response) {
      success("Price update request rejected successfully");
      await fetchDetails();
    } else {
      error("Failed to reject price update request");
    }
    setLoading(false);
  };

  const handleStatusChange = async (status: string) => {
    if (
      !window.confirm(`Are you sure you want to change status to ${status}?`)
    ) {
      return;
    }
    setLoading(true);
    const apiService = new APICallService(
      priceUpdateRequest.changeStatus,
      { status: status },
      { id: state.id },
      "",
      "",
      "",
      All_Products,
      selectedWarehouse?.value || null
    );
    const response = await apiService.callAPI();
    if (response) {
      success("Status updated successfully");
      await fetchDetails();
    } else {
      error("Failed to update status");
    }
    setLoading(false);
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case PriceRequestAccepted.toString():
        return <span className="badge badge-success fs-14">Approved</span>;
      case PriceRequestRejected.toString():
        return <span className="badge badge-danger fs-14">Rejected</span>;

      default:
        return <span className="badge badge-secondary fs-14">Pending</span>;
    }
  };

  if (fetchLoader) {
    return (
      <div className="p-5">
        <div className="d-flex justify-content-center align-items-center min-h-400px">
          <Loader loading={fetchLoader} />
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="p-5">
        <Card className="border border-r10px">
          <Card.Body className="p-6 text-center">
            <p className="fs-16 fw-500">No details found</p>
            <Button className="uw-btn uw-btn-primary"
              variant="primary"
              onClick={() => navigate("/price-update-request")}
            >
              Back to List
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-5">
      <Row className="align-items-center g-md-5 g-3 mb-7">
        <Col xs>
          <h1 className="fs-22 fw-bolder">Price Update Request Details</h1>
        </Col>
        {/* <Col xs="auto">
          <Button
            variant="outline-secondary"
            onClick={() => navigate("/price-update-request")}
          >
            Back to List
          </Button>
        </Col> */}
      </Row>

      <Card className="border border-r10px mb-7">
        <Card.Body className="p-6">
          <Row className="g-4">
            <Col md={6}>
              <div className="mb-4">
                <label className="fs-14 fw-600 text-muted d-block mb-2">
                  Request ID
                </label>
                <span className="fs-16 fw-500 text-dark">
                  {details.refKey || details._id?.substring(0, 8)}
                </span>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-4">
                <label className="fs-14 fw-600 text-muted d-block mb-2">
                  Request Date
                </label>
                <span className="fs-16 fw-500 text-dark">
                  {Method.convertDateToDDMMYYYYHOURS(details._createdAt)}
                </span>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-4">
                <label className="fs-14 fw-600 text-muted d-block mb-2">
                  Status
                </label>
                <div>{getStatusBadge(details.status.toString())}</div>
              </div>
            </Col>
            {details.product && (
              <>
                <Col md={6}>
                  <div className="mb-4">
                    <label className="fs-14 fw-600 text-muted d-block mb-2">
                      Product Name
                    </label>
                    <span className="fs-16 fw-500 text-dark">
                      {details.variant.title || "N/A"}
                    </span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-4">
                    <label className="fs-14 fw-600 text-muted d-block mb-2">
                      Product ID
                    </label>
                    <span className="fs-16 fw-500 text-dark">
                      {details.variant.refKey || "N/A"}
                    </span>
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Price Comparison Section - Similar to product-details pricedimension */}
      {details.oldPriceChanges &&
        details.oldPriceChanges.length > 0 &&
        details.newPriceChanges &&
        details.newPriceChanges.length > 0 && (
          <>
            {details.oldPriceChanges.map(
              (oldPriceChange: any, index: number) => {
                const newPriceChange =
                  details.newPriceChanges[index] ||
                  details.newPriceChanges.find(
                    (npc: any) => npc.type === oldPriceChange.type
                  );
                if (!newPriceChange) return null;

                const getQuantityTypeLabel = (type: number) => {
                  if (type === CartonWithDozens || type === CartonWithPieces)
                    return "Carton details";
                  if (type === Dozen) return "Dozen details";
                  if (type === Piece) return "Piece details";
                  return "Quantity details";
                };

                const getQuantityTypeUnit = (type: number) => {
                  if (type === CartonWithDozens || type === CartonWithPieces)
                    return "Carton";
                  if (type === Dozen) return "Dozen";
                  if (type === Piece) return "Piece";
                  return "";
                };

                const getQuantityTypeUnitLower = (type: number) => {
                  if (type === CartonWithDozens || type === CartonWithPieces)
                    return "carton";
                  if (type === Dozen) return "dozen";
                  if (type === Piece) return "piece";
                  return "";
                };

                const oldPrice =
                  oldPriceChange.discountAmtEnabled &&
                  oldPriceChange.discountAmt
                    ? oldPriceChange.discountAmt
                    : oldPriceChange.amount;

                const newPrice =
                  newPriceChange.discountAmtEnabled &&
                  newPriceChange.discountAmt
                    ? newPriceChange.discountAmt
                    : newPriceChange.amount;

                return (
                  <Card key={index} className="border mb-8">
                    <Card.Header className="bg-light align-items-center d-flex justify-content-between">
                      <h3 className="fs-22 fw-bolder mb-0">
                        <span>
                          <img src={carton} width={27} height={24} alt="" />
                        </span>{" "}
                        {getQuantityTypeLabel(
                          newPriceChange.type || oldPriceChange.type
                        )}
                      </h3>
                      <Col sm="auto">
                        {newPriceChange.discountAmtEnabled ? (
                          <>
                            <span className="fs-22 fw-600">
                              TSh{" "}
                              {Method.getGeneralizedAmount(
                                newPriceChange.discountAmt ||
                                  newPriceChange.amount
                              )}
                            </span>
                            <span className="fs-18 fw-500 text-gray text-decoration-line-through ms-3">
                              TSh{" "}
                              {Method.getGeneralizedAmount(
                                newPriceChange.amount
                              )}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="fs-22 fw-600">
                              TSh{" "}
                              {Method.getGeneralizedAmount(
                                newPriceChange.amount
                              )}
                            </span>
                            {oldPriceChange.amount !==
                              newPriceChange.amount && (
                              <span className="fs-18 fw-500 text-gray text-decoration-line-through ms-3">
                                TSh{" "}
                                {Method.getGeneralizedAmount(
                                  oldPriceChange.amount
                                )}
                              </span>
                            )}
                          </>
                        )}
                      </Col>
                    </Card.Header>
                    <Card.Body>
                      <Row className="mb-3">
                        <Col lg={6} className="d-flex align-items-center gap-2">
                          <label className="fs-16 fw-500 text-dark mb-0 min-w-180px">
                            Original price:
                          </label>
                          <span className="fw-bold fs-16 fw-600 text-dark">
                            TSh {Method.getGeneralizedAmount(oldPriceChange.amount)} / Dozen
                          </span>
                        </Col>

                        <Col lg={6} className="d-flex align-items-center gap-2">
                          <label className="fs-16 fw-500 text-dark mb-0 min-w-250px">
                            Original discount price:
                          </label>
                          <span className="fw-bold fs-16 fw-600 text-dark">
                            {oldPriceChange?.discountAmtEnabled
                              ? `TSh ${Method.getGeneralizedAmount(oldPriceChange.discountAmt)} / Dozen`
                              : "-"}
                          </span>
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col lg={6} className="d-flex align-items-center gap-2">
                          <label className="fs-16 fw-500 text-dark mb-0 min-w-180px">
                            Requested price:
                          </label>
                          <span className="fw-bold fs-16 fw-600 text-dark">
                            TSh {Method.getGeneralizedAmount(newPriceChange.amount)} / Dozen
                          </span>
                        </Col>

                        <Col lg={6} className="d-flex align-items-center gap-2">
                          <label className="fs-16 fw-500 text-dark mb-0 min-w-250px">
                            Requested discount price:
                          </label>
                          <span className="fw-bold fs-16 fw-600 text-dark">
                            {newPriceChange.discountAmt ? `TSh ${Method.getGeneralizedAmount(newPriceChange.discountAmt)} / Dozen` : "-"}
                          </span>
                        </Col>
                      </Row>
                      {newPriceChange.discountByQuantitiesEnabled &&
                      newPriceChange.discountsByQuantities &&
                      newPriceChange.discountsByQuantities.length > 0 ? (
                        <>
                          <Row className="mb-7">
                            <Col lg={2}>
                              <label className="fs-16 fw-500 text-dark">
                                Discount by total number of quantities:
                              </label>
                            </Col>
                            <Col lg={"auto"}>
                              <Table
                                responsive
                                className="table-rounded table-row-bordered border align-middle border-r10px gs-5 mb-0"
                              >
                                <thead>
                                  <tr className="fs-16 fw-bold">
                                    <th>Minimum qty.</th>
                                    <th>Maximum qty.</th>
                                    <th>
                                      Discounted price of a{" "}
                                      {getQuantityTypeUnitLower(
                                        newPriceChange.type ||
                                          oldPriceChange.type
                                      )}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {newPriceChange.discountsByQuantities.map(
                                    (disVal: any, disIndex: number) => {
                                      return (
                                        <tr key={disIndex}>
                                          <td>
                                            <span className="fs-16 fw-500">
                                              {disVal.min}{" "}
                                              {getQuantityTypeUnitLower(
                                                newPriceChange.type ||
                                                  oldPriceChange.type
                                              )}
                                            </span>
                                          </td>
                                          <td>
                                            <span className="fs-16 fw-500">
                                              {disVal.max}{" "}
                                              {getQuantityTypeUnitLower(
                                                newPriceChange.type ||
                                                  oldPriceChange.type
                                              )}
                                            </span>
                                          </td>
                                          <td>
                                            <span className="fs-16 fw-500">
                                              TSh{" "}
                                              {Method.getGeneralizedAmount(
                                                disVal.discountAmt
                                              )}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    }
                                  )}
                                </tbody>
                              </Table>
                            </Col>
                          </Row>
                        </>
                      ) : (
                        <></>
                      )}
                      {newPriceChange.dimensions && (
                        <Row className="align-items-center">
                          <Col lg={2}>
                            <label className="fs-16 fw-500 text-dark">
                              {getQuantityTypeUnit(
                                newPriceChange.type || oldPriceChange.type
                              )}{" "}
                              dimensions:
                            </label>
                          </Col>
                          <Col lg={10}>
                            <div className="d-flex flex-lg-row flex-wrap align-items-center">
                              {newPriceChange.dimensions.length_ > 0 && (
                                <div className="d-flex justify-content-center align-items-center w-fit-content bg-gray-100 p-4 px-4 pills-radius me-3 mb-3">
                                  <span className="fw-600 text-black fs-16">
                                    Length: {newPriceChange.dimensions.length_}{" "}
                                    cm
                                  </span>
                                </div>
                              )}
                              {newPriceChange.dimensions.width > 0 && (
                                <div className="d-flex justify-content-center align-items-center w-fit-content bg-gray-100 p-4 px-4 pills-radius me-3 mb-3">
                                  <span className="fw-600 text-black fs-16">
                                    Width: {newPriceChange.dimensions.width} cm
                                  </span>
                                </div>
                              )}
                              {newPriceChange.dimensions.height > 0 && (
                                <div className="d-flex justify-content-center align-items-center w-fit-content bg-gray-100 p-4 px-4 pills-radius me-3 mb-3">
                                  <span className="fw-600 text-black fs-16">
                                    Height: {newPriceChange.dimensions.height}{" "}
                                    cm
                                  </span>
                                </div>
                              )}
                              {newPriceChange.dimensions.weight > 0 && (
                                <div className="d-flex justify-content-center align-items-center w-fit-content bg-gray-100 p-4 px-4 pills-radius me-3 mb-3">
                                  <span className="fw-600 text-black fs-16">
                                    Weight: {newPriceChange.dimensions.weight}{" "}
                                    kg
                                  </span>
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      )}
                    </Card.Body>
                  </Card>
                );
              }
            )}
          </>
        )}

      <Card className="border border-r10px mb-7">
        <Card.Body className="p-6">
          <Row className="g-4">
            {details.requestedBy && (
              <Col md={6}>
                <div className="mb-4">
                  <label className="fs-14 fw-600 text-muted d-block mb-2">
                    Requested By
                  </label>
                  <span className="fs-16 fw-500 text-dark">
                    {details.requestedBy.name ||
                      details.requestedBy.email ||
                      "N/A"}
                  </span>
                </div>
              </Col>
            )}
            {details.approvedBy && (
              <Col md={6}>
                <div className="mb-4">
                  <label className="fs-14 fw-600 text-muted d-block mb-2">
                    Approved By
                  </label>
                  <span className="fs-16 fw-500 text-dark">
                    {details.approvedBy.name ||
                      details.approvedBy.email ||
                      "N/A"}
                  </span>
                </div>
              </Col>
            )}
            {details.approvedAt && (
              <Col md={6}>
                <div className="mb-4">
                  <label className="fs-14 fw-600 text-muted d-block mb-2">
                    Approved At
                  </label>
                  <span className="fs-16 fw-500 text-dark">
                    {Method.convertDateToDDMMYYYYHOURS(details.approvedAt)}
                  </span>
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {details.status == PriceRequestSubmitted &&
        Method.hasPermission(All_Products, Edit, currentUser) && (
          <Card className="border border-r10px">
            <Card.Body className="p-6">
              <Row>
                <Col xs={12}>
                  <h5 className="fs-18 fw-bold mb-4">Actions</h5>
                  <div className="d-flex gap-3">
                    <Button className="uw-btn uw-btn-success"
                      variant="success"
                      onClick={handleApprove}
                      disabled={loading}
                    >
                      {loading ? "Processing..." : "Approve Request"}
                    </Button>
                    <Button className="uw-btn uw-btn-danger"
                      variant="danger"
                      onClick={handleReject}
                      disabled={loading}
                    >
                      {loading ? "Processing..." : "Reject Request"}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
    </div>
  );
};

export default PriceUpdateRequestDetail;
